/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: no any
import {
  entityConfigs,
  EntityIdMap,
  EntityType,
} from '@/lib/entities/entityConfig';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState, useMemo } from 'react';
import usePromise from '@/hooks/usePromise';
import { FieldComponentProps } from './types';
import { useScrollIntoViewOnFocus } from '@/hooks/useScrollIntoViewOnFocus';

// View component for displaying the selected entity
function FieldView<K extends EntityType>({
  entityType,
  entityId,
  placeholder = 'No selection',
}: {
  entityType: K;
  entityId: EntityIdMap[K];
  placeholder?: string;
}) {
  const entityConfig = entityConfigs[entityType];
  const entity = usePromise(
    useMemo(
      () => entityConfig.getById(String(entityId)),
      [entityId, entityConfig],
    ) as Promise<any>,
  );

  if (!entity && entityId) {
    return <Skeleton className="h-4 w-32" />;
  }

  if (!entity) {
    return <p className="text-muted-foreground">{placeholder}</p>;
  }

  return <div>{entityConfig.renderBadge(entity as any)}</div>;
}

function FieldEdit<K extends EntityType>({
  entityType,
  value,
  onChange,
  placeholder,
}: {
  entityType: K;
  value: EntityIdMap[K];
  onChange: (value: EntityIdMap[K] | null) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const fieldRef = useScrollIntoViewOnFocus<HTMLButtonElement>({
    bufferPx: 400,
  });

  const entityConfig = entityConfigs[entityType];

  // Use usePromise with search function
  const searchResults =
    usePromise(
      useMemo(
        () => entityConfig.search(searchTerm),
        [searchTerm, entityConfig],
      ) as Promise<any[]>,
    ) || [];

  const isSearching = !searchResults && searchTerm !== '';

  // Get the currently selected entity for display
  const selectedEntity = usePromise(
    useMemo(
      () =>
        value ? entityConfig.getById(String(value)) : Promise.resolve(null),
      [value, entityConfig],
    ) as Promise<any>,
  );

  const handleSelect = (entityId: EntityIdMap[K] | null) => {
    onChange(entityId);
    setOpen(false);
    setSearchTerm('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={fieldRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-8 w-full justify-between px-2 text-sm',
            !selectedEntity && 'text-muted-foreground',
          )}
        >
          {selectedEntity
            ? entityConfig.renderBadge(selectedEntity as any)
            : placeholder || `Select ${entityType}`}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command
          filter={(value, search, keywords) => {
            if (!search) return 1;
            const needle = search.toLowerCase();
            const haystack = [value, ...(keywords ?? [])]
              .join(' ')
              .toLowerCase();
            return haystack.includes(needle) ? 1 : 0;
          }}
        >
          {entityType === 'user' && (
            <CommandInput
              placeholder={`Search users...`}
              value={searchTerm}
              onValueChange={setSearchTerm}
              className="h-9"
              autoFocus
            />
          )}
          <CommandList>
            <CommandEmpty>
              {isSearching ? 'Searching...' : `No ${entityType}s found.`}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem value="none" onSelect={() => handleSelect(null)}>
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === null ? 'opacity-100' : 'opacity-0',
                  )}
                />
                No {entityType}
              </CommandItem>
              {searchResults.map((entity: any) => (
                <CommandItem
                  key={entityConfig.getOptionValue(entity)}
                  keywords={entityConfig.getOptionKeywords(entity as any)}
                  value={String(entityConfig.getOptionValue(entity))}
                  onSelect={() =>
                    handleSelect(
                      entityConfig.getOptionValue(entity) as EntityIdMap[K],
                    )
                  }
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === entityConfig.getOptionValue(entity)
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  {entityConfig.renderBadge(entity)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function ForeignKeyField({
  formMode,
  controllerField,
  field,
}: FieldComponentProps) {
  const entityType = field.meta.entity as EntityType;

  if (!entityType) {
    return (
      <div className="text-destructive bg-destructive/10 rounded-md p-2 text-sm">
        ForeignKey field "{field.name}" is missing entity type "{entityType}"
      </div>
    );
  }

  if (formMode === 'loading') {
    return <Skeleton className="h-8 w-full" />;
  }

  if (formMode === 'edit') {
    return (
      <FieldEdit
        entityType={entityType}
        value={controllerField.value}
        onChange={controllerField.onChange}
        placeholder={field.meta.placeholder || `Select ${entityType}`}
      />
    );
  }

  return (
    <FieldView
      entityType={entityType}
      entityId={controllerField.value}
      placeholder={field.meta.placeholder || 'No selection'}
    />
  );
}
